import { useEffect, useMemo, useState } from 'react';
import { formatNumber } from '../lib/actions';
import { api } from '../lib/api';
import { RecordingPlayer } from './RecordingPlayer';
import { Button, duration, Icon, Panel } from './ui';

interface RecordingRow {
  id: string;
  interaction_id: string;
  kind: 'call' | 'voicemail';
  duration_seconds: number | null;
  created_at: string;
  direction: 'inbound' | 'outbound';
  customer_number: string | null;
  customer_name: string | null;
  queue_name: string | null;
  agent_name: string | null;
  disposition_label: string | null;
}
interface Named {
  id: string;
  name: string;
}

const LENGTHS = [
  { label: 'Any length', min: '' },
  { label: 'Over 30 seconds', min: '30' },
  { label: 'Over 1 minute', min: '60' },
  { label: 'Over 5 minutes', min: '300' },
];
const PAGE = 50;
const select = 'rounded-lg border border-line bg-abyss/50 px-2.5 py-2 text-sm outline-none focus:border-ion';

function useList<T>(path: string): T[] {
  const [rows, setRows] = useState<T[]>([]);
  useEffect(() => {
    api<T[]>('GET', path)
      .then(setRows)
      .catch(() => setRows([]));
  }, [path]);
  return rows;
}

/** Recording search and playback for supervisors and QA (PRD QA-02, QA-03). */
export function Recordings() {
  const agents = useList<Named & { role: string }>('/users').filter((u) => u.role === 'agent' || u.role === 'supervisor');
  const queues = useList<Named>('/queues');
  const outcomes = useList<{ id: string; label: string }>('/dispositions');
  const [f, setF] = useState({ from: '', to: '', agentId: '', queueId: '', dispositionId: '', number: '', minSeconds: '', kind: '' });
  const [rows, setRows] = useState<RecordingRow[] | null>(null);
  const [more, setMore] = useState(false);
  const [open, setOpen] = useState<RecordingRow | null>(null);

  const query = useMemo(() => {
    const q = new URLSearchParams({ limit: String(PAGE) });
    for (const [k, v] of Object.entries(f)) {
      const value = k === 'number' ? v.replace(/\D/g, '') : v;
      if (value && (k !== 'number' || value.length >= 3)) q.set(k, value);
    }
    return q;
  }, [f]);

  useEffect(() => {
    let live = true;
    const t = window.setTimeout(() => {
      api<RecordingRow[]>('GET', `/recordings?${query}`)
        .then((r) => {
          if (!live) return;
          setRows(r);
          setMore(r.length === PAGE);
        })
        .catch(() => live && setRows([]));
    }, 250);
    return () => {
      live = false;
      window.clearTimeout(t);
    };
  }, [query]);

  async function loadMore() {
    if (!rows?.length) return;
    const q = new URLSearchParams(query);
    q.set('before', rows[rows.length - 1].created_at);
    const next = await api<RecordingRow[]>('GET', `/recordings?${q}`).catch(() => []);
    setRows([...rows, ...next]);
    setMore(next.length === PAGE);
  }

  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const who = (r: RecordingRow) => r.customer_name ?? (r.customer_number ? formatNumber(r.customer_number) : 'Unknown caller');

  return (
    <div className="mx-auto flex max-w-[1200px] flex-col gap-4">
      <Panel className="p-5">
        <div className="mb-4">
          <h1 className="text-lg font-medium">Recordings</h1>
          <p className="text-sm text-ink-3">Calls and voicemails still kept under your retention settings. Caller and agent are on separate tracks.</p>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-8">
          <label className="flex flex-col gap-1 text-xs text-ink-3">
            From
            <input type="date" className={select} value={f.from} onChange={set('from')} />
          </label>
          <label className="flex flex-col gap-1 text-xs text-ink-3">
            To
            <input type="date" className={select} value={f.to} onChange={set('to')} />
          </label>
          <label className="flex flex-col gap-1 text-xs text-ink-3">
            Agent
            <select className={select} value={f.agentId} onChange={set('agentId')}>
              <option value="">All agents</option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-ink-3">
            Queue
            <select className={select} value={f.queueId} onChange={set('queueId')}>
              <option value="">All queues</option>
              {queues.map((q) => (
                <option key={q.id} value={q.id}>
                  {q.name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-ink-3">
            Outcome
            <select className={select} value={f.dispositionId} onChange={set('dispositionId')}>
              <option value="">Any outcome</option>
              {outcomes.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-ink-3">
            Length
            <select className={select} value={f.minSeconds} onChange={set('minSeconds')}>
              {LENGTHS.map((l) => (
                <option key={l.label} value={l.min}>
                  {l.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-ink-3">
            Type
            <select className={select} value={f.kind} onChange={set('kind')}>
              <option value="">Calls and voicemail</option>
              <option value="call">Calls</option>
              <option value="voicemail">Voicemail</option>
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-ink-3">
            Number
            <input inputMode="tel" placeholder="Any part" className={`${select} tabular`} value={f.number} onChange={set('number')} />
          </label>
        </div>
      </Panel>

      {open ? <RecordingPlayer key={open.id} recordingId={open.id} title={`${who(open)}, ${new Date(open.created_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}`} /> : null}

      <Panel className="overflow-x-auto p-0">
        <table className="w-full min-w-[760px] text-left text-sm">
          <thead className="text-ink-3">
            <tr className="border-b border-line">
              <th className="px-4 py-2.5 font-normal" aria-label="Play" />
              <th className="px-4 py-2.5 font-normal">When</th>
              <th className="px-4 py-2.5 font-normal">Caller</th>
              <th className="px-4 py-2.5 font-normal">Agent</th>
              <th className="px-4 py-2.5 font-normal">Queue</th>
              <th className="px-4 py-2.5 font-normal">Outcome</th>
              <th className="px-4 py-2.5 text-right font-normal">Length</th>
            </tr>
          </thead>
          <tbody>
            {(rows ?? []).map((r) => (
              <tr
                key={r.id}
                tabIndex={0}
                onClick={() => setOpen(r)}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), setOpen(r))}
                aria-selected={open?.id === r.id}
                className={`cursor-pointer border-b border-line/60 last:border-0 hover:bg-veil/[0.04] ${open?.id === r.id ? 'bg-ion/[0.08]' : ''}`}
              >
                <td className="w-12 px-4 py-2.5">
                  <span className={`grid h-7 w-7 place-items-center rounded-lg border ${open?.id === r.id ? 'border-ion text-ion' : 'border-line text-ink-2'}`}>
                    <Icon name="play" size={12} />
                  </span>
                </td>
                <td className="whitespace-nowrap px-4 py-2.5 text-ink-2">{new Date(r.created_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</td>
                <td className="px-4 py-2.5">
                  {who(r)}
                  <span className="ml-1.5 text-xs text-ink-3">{r.kind === 'voicemail' ? 'voicemail' : r.direction === 'outbound' ? 'outbound' : ''}</span>
                </td>
                <td className="px-4 py-2.5">{r.agent_name ?? <span className="text-ink-3">None</span>}</td>
                <td className="px-4 py-2.5">{r.queue_name ?? <span className="text-ink-3">None</span>}</td>
                <td className="px-4 py-2.5">{r.disposition_label ?? <span className="text-ink-3">None</span>}</td>
                <td className="tabular px-4 py-2.5 text-right">{duration(r.duration_seconds ?? 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows?.length === 0 ? <p className="px-4 py-10 text-center text-sm text-ink-2">No recordings match these filters.</p> : null}
        {rows === null ? <p className="px-4 py-10 text-center text-sm text-ink-3">Loading…</p> : null}
        {more ? (
          <div className="border-t border-line p-3 text-center">
            <Button tone="quiet" className="px-3 py-1.5 text-sm" onClick={() => void loadMore()}>
              Show older
            </Button>
          </div>
        ) : null}
      </Panel>
    </div>
  );
}
